import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Award, Star, Zap, Trophy, TrendingUp } from 'lucide-react';

import { getMyBadges } from '../../services/trainingService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { formatDate } from '../../utils/Utils';

const Container = styled.div`padding: 16px 24px 24px;`;

const TopStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, ${({ $from }) => $from}, ${({ $to }) => $to});
  color: #fff;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StatIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatLabel = styled.div`font-size: 13px; opacity: 0.85;`;
const StatValue = styled.div`font-size: 28px; font-weight: 800; line-height: 1.1;`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const CardTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #004242;
  margin: 0 0 16px;
`;

const BadgeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
`;

const BadgeCard = styled.div`
  border: 1.5px solid ${({ $earned }) => ($earned ? '#10B981' : '#E2E8F0')};
  background: ${({ $earned }) => ($earned ? '#F0FDF4' : '#F8FAFC')};
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  opacity: ${({ $earned }) => ($earned ? 1 : 0.6)};
`;

const BadgeIcon = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: ${({ $earned }) => ($earned ? 'linear-gradient(135deg, #10B981, #34D399)' : '#E2E8F0')};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BadgeName = styled.div`
  font-weight: 700;
  color: #004242;
  font-size: 14px;
  margin-bottom: 4px;
`;

const BadgeDesc = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
  margin-bottom: 6px;
`;

const BadgeEarnedAt = styled.div`
  font-size: 11px;
  color: #16A34A;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #94a3b8;
`;

// Map badge codes to icons
const BADGE_ICONS = {
  first_step: Star,
  perfect_score: Trophy,
  speed_master: Zap,
  streak3: TrendingUp,
};

export default function MyBadgesPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getMyBadges()
      .then((res) => setData(res.data?.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Container>{t('common.loading')}</Container>;
  if (!data) return <Container>{t('common.noData')}</Container>;

  const badges = data.badges || [];

  return (
    <Container>
      <Breadcrumbs items={[{ label: t('gamification.myBadges') }]} />

      <TopStats>
        <StatCard $from="#10B981" $to="#34D399">
          <StatIcon><Award size={26} /></StatIcon>
          <div>
            <StatLabel>{t('gamification.earnedBadges')}</StatLabel>
            <StatValue>{badges.length}</StatValue>
          </div>
        </StatCard>
        <StatCard $from="#0291EF" $to="#38BDF8">
          <StatIcon><TrendingUp size={26} /></StatIcon>
          <div>
            <StatLabel>{t('gamification.totalPoints')}</StatLabel>
            <StatValue>{data.totalPoints || 0}</StatValue>
          </div>
        </StatCard>
        <StatCard $from="#F59E0B" $to="#FBBF24">
          <StatIcon><Trophy size={26} /></StatIcon>
          <div>
            <StatLabel>{t('gamification.thisMonthPoints')}</StatLabel>
            <StatValue>{data.thisMonthPoints || 0}</StatValue>
          </div>
        </StatCard>
      </TopStats>

      <Card>
        <CardTitle>{t('gamification.myBadgesList')}</CardTitle>
        {badges.length === 0 ? (
          <EmptyState>
            <Award size={42} style={{ marginBottom: 12, opacity: 0.4 }} />
            <div>{t('gamification.noBadgesYet')}</div>
          </EmptyState>
        ) : (
          <BadgeGrid>
            {badges.map((b) => {
              const Icon = BADGE_ICONS[b.code] || Award;
              return (
                <BadgeCard key={b.id} $earned>
                  <BadgeIcon $earned>
                    <Icon size={28} />
                  </BadgeIcon>
                  <BadgeName>{b.name}</BadgeName>
                  <BadgeDesc>{b.description}</BadgeDesc>
                  <BadgeEarnedAt>
                    {t('gamification.earnedOn', { date: formatDate(b.awardedAt, i18n.language) })}
                  </BadgeEarnedAt>
                </BadgeCard>
              );
            })}
          </BadgeGrid>
        )}
      </Card>
    </Container>
  );
}
